/* eslint-disable react-hooks/exhaustive-deps */
import { useFormik } from "formik";
import { Methods } from "./methods";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import { toast } from "react-toastify";
import { Categories } from "./categories";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import * as api from "../../../shared/lib/api";
import JsonCodeEditor from "../../../shared/components/json-code-editor";
import ListItem from "@mui/material/ListItem";
import MenuItem from "@mui/material/MenuItem";
import { RequestTypes } from "./request_types";
import { ToastContainer } from "react-toastify";
import TextField from "@mui/material/TextField";
import { useCore } from "../../../shared/hooks/use-core";
import { ResponseTypes } from "./response_types";
import { AuthTypes } from "./auth_types";
import InputLabel from "@mui/material/InputLabel";
import IconButton from "@mui/material/IconButton";
import { LooseObject } from "../../../shared/types/core";
import FormControl from "@mui/material/FormControl";
import DeleteIcon from "@mui/icons-material/Delete";
import ListItemText from "@mui/material/ListItemText";
import { palette } from "../../../shared/styles/theme";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import { useLocation, useParams } from "react-router-dom";
import {
	MercosulRetryProps,
	ParameterProps,
} from "../../../features/api/types/parameter";
import { dysplayValueType, ValuesTypes } from "./value_type";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { dysplayLocal, LocalParameter } from "./local_parameters";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import {
	ApiFailureRuleMatch,
	ApiProps,
	ParameterDescription,
	RetrieveApiProps,
} from "../../../features/api/types/api";
import FailureConditionsBuilder, {
	describeMatch,
} from "../../../features/api/components/failure-conditions-builder";
import FailureRuleBuilder from "../../../features/api/components/failure-rule-builder";
import ModalDelete from "../../../shared/components/modal-delete";
import Divider from "@mui/material/Divider";
import { useAuth } from "../../../features/auth/hooks/use-auth";
import { extractApiErrorMessage } from "../../../shared/utils/extract-api-error-message";
import { FailureRule } from "../../../features/company/types/company-api-failure-message";
import {
	ApiDefaultFailureMessageProps,
	ListApiDefaultFailureMessageProps,
} from "../../../features/api/types/api-default-failure-message";
import {
	Alert,
	Autocomplete,
	Box,
	Checkbox,
	FormControlLabel,
	FormGroup,
	FormHelperText,
	ListItemButton,
	ListItemSecondaryAction,
	Modal,
	Switch,
	Typography,
} from "@mui/material";

interface ModalInfoProps {
	open: boolean;
	item: ParameterProps | undefined;
}

type DefaultFailureMessageSectionProps = {
	apiId: string;
};

export function DefaultFailureMessageSection({
	apiId,
}: DefaultFailureMessageSectionProps): JSX.Element {
	const [message, setMessage] = useState<ApiDefaultFailureMessageProps | null>(
		null,
	);
	const [rules, setRules] = useState<FailureRule[]>([]);
	const [saving, setSaving] = useState<boolean>(false);
	const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
	const [deleting, setDeleting] = useState<boolean>(false);

	const getMessage = useCallback(async () => {
		const response = (await api.get(
			`api/default-failure-message/list/?api=${apiId}`,
		)) as ListApiDefaultFailureMessageProps;
		const current = response.content.results[0] ?? null;
		setMessage(current);
		setRules(current?.rules ?? []);
	}, [apiId]);

	useEffect(() => {
		getMessage().catch(console.error);
	}, [getMessage]);

	async function createMessage() {
		if (rules.length === 0) {
			toast.error("Adicione pelo menos uma regra.", {
				position: toast.POSITION.TOP_RIGHT,
			});
			return;
		}

		setSaving(true);
		const response = (await api.post("api/default-failure-message/create/", {
			api: apiId,
			rules,
		})) as { [key: string]: any };
		setSaving(false);

		if (response.status === 201) {
			toast.success("Mensagem de erro padrão cadastrada com sucesso!", {
				position: toast.POSITION.TOP_RIGHT,
			});
			getMessage().catch(console.error);
		} else {
			toast.error(extractApiErrorMessage(response), {
				position: toast.POSITION.TOP_RIGHT,
			});
		}
	}

	async function updateMessage() {
		setSaving(true);
		const response = (await api.put(
			`api/default-failure-message/update/${message!.id}/`,
			{ rules },
		)) as { [key: string]: any };
		setSaving(false);

		if (response.status === 200) {
			toast.success("Regras atualizadas com sucesso!", {
				position: toast.POSITION.TOP_RIGHT,
			});
		} else {
			toast.error(extractApiErrorMessage(response), {
				position: toast.POSITION.TOP_RIGHT,
			});
		}
	}

	async function confirmDelete() {
		setDeleting(true);
		const response = (await api.destroy(
			`api/default-failure-message/destroy/${message!.id}/`,
		)) as { [key: string]: any };
		setDeleting(false);
		setDeleteOpen(false);

		if (response.status === 204) {
			setMessage(null);
			setRules([]);
			toast.success("Mensagem de erro padrão excluída com sucesso!", {
				position: toast.POSITION.TOP_RIGHT,
			});
		} else {
			toast.error("Não foi possível excluir essa configuração.", {
				position: toast.POSITION.TOP_RIGHT,
			});
		}
	}

	return (
		<>
			<Divider sx={{ marginY: 5 }} />
			<Grid container spacing={2}>
				<Grid item md={12} xs={12}>
					<h3>Mensagem de erro padrão</h3>
					<Typography variant="body2" color="text.secondary">
						Copiada automaticamente para as empresas novas no momento em que são
						cadastradas.
					</Typography>
				</Grid>
				<Grid item md={12} xs={12}>
					<FailureRuleBuilder rules={rules} onChange={setRules} />
					{message ? (
						<Box sx={{ display: "flex", gap: 1, marginTop: 2 }}>
							<Button
								variant="contained"
								color="success"
								disabled={saving}
								onClick={updateMessage}
							>
								Salvar
							</Button>
							<Button
								variant="outlined"
								color="error"
								onClick={() => setDeleteOpen(true)}
							>
								Excluir
							</Button>
						</Box>
					) : (
						<Button
							fullWidth
							variant="contained"
							size="large"
							color="success"
							disabled={saving}
							onClick={createMessage}
							sx={{ marginTop: 2 }}
						>
							Adicionar
						</Button>
					)}
				</Grid>
			</Grid>

			<ModalDelete
				open={deleteOpen}
				textBody="Tem certeza que deseja excluir a mensagem de erro padrão desta API?"
				handleModalClose={() => setDeleteOpen(false)}
				handleDelete={confirmDelete}
				loading={deleting}
			/>
		</>
	);
}

function collectMessages(value: unknown): string[] {
	if (typeof value === "string") return [value];
	if (Array.isArray(value)) return value.flatMap(collectMessages);
	if (value && typeof value === "object")
		return Object.values(value).flatMap(collectMessages);
	return [];
}

function extractParametersError(response: { data?: any }): string | undefined {
	const errors =
		response?.data?.content?.parameters ?? response?.data?.parameters;
	const messages = collectMessages(errors);
	return messages.length > 0 ? messages.join(" ") : undefined;
}

export default function CreateOrUpdateApi(): JSX.Element {
	const location: LooseObject = useLocation();
	let { id, provider_id } = useParams();
	const { setTitleBar, setPathTitleBar } = useCore();
	const { user } = useAuth();
	const isAdmin = user?.profile === "3";

	// Dados a API
	const [urlTokenApis, setUrlTokenApis] = useState<string>();
	const [urlListApis, setUrlListApis] = useState<string>();
	const [category, setCategory] = useState<string>("1");
	const [requestType, setRequestType] = useState<string>("1");
	const [method, setMethod] = useState<string>("1");
	const [responseType, setResponseType] = useState<string>("1");
	const [authType, setAuthType] = useState<string>("1");
	const [dynamicToken, setDynamicToken] = useState<boolean>(false);
	const [parameters, setParameters] = useState<ParameterProps[]>([]);
	const [newMercosulRetry, setNewMercosulRetry] = useState<MercosulRetryProps>({
		enabled: false,
		when: [],
	});
	const [parametersError, setParametersError] = useState<string | null>(null);
	const [listParameterDescription, setListParameterDescription] = useState<
		ParameterDescription[]
	>([]);
	const [dynamicsTokens, setDynamicsTokens] = useState<ApiProps[]>([]);
	// Dados dos parametros da API
	const [localParameter, setLocalParameter] = useState<string>("1");
	const [fixedParameter, setFixedParameter] = useState<string>("0");
	const [valueTypeParameter, setValueTypeParameter] = useState<
		"1" | "2" | "3" | "4" | "5" | "6"
	>("1");
	const [required, setRequired] = useState<string>("1");
	const [openInfo, setOpenInfo] = useState<number>(-1);
	//opções de lista categoria 3
	const [apiListOptions, setApiListOptions] = useState<ApiProps[]>([]);

	const { handleChange, handleSubmit, values, errors, setFieldValue, touched } =
		useFormik({
			initialValues: {
				url: "",
				name: "",
				webhook: false,
				description: "",
				jsonstructure: "",
				boolean_value: "",
				parameter_name: "",
				parameter_value: "",
				boolean_default: "",
				dynamic_token_id: "",
				parameter_description: "",
				cache_enabled: false,
				value_list_parameter_label: "0",
				value_list_parameter_dependence: "0",
				api_list_parameter: "0",
				api_list_parameter_dependence: "0",
				cache_ttl_days: 0,
				cache_required_response_paths: "",
				timeout_seconds: 180,
				failure_rule: [] as ApiFailureRuleMatch[],
				use_list_api: "0", // '1' = sim, '0' = não
				list_api_id: "", // id da API de lista selecionada
				ui_position: 1, // ordem do select na UI dinâmica (1,2,3...)
				ui_depends_on: [] as string[], // depende de quais campos já preenchidos
				group_enabled: "0", // '1' = agrupar, '0' = não
				group_key: "", // ex: "reference"
				group_label: "",
			},
			onSubmit: createOrUpdateApi,
		});

	// API function
	const handleCategoryChange = (event: SelectChangeEvent) => {
		setCategory(event.target.value as string);
		if (!id) {
			setDynamicToken(false);
			setFieldValue("dynamic_token_id", "");
			setParameters([]);
		}
	};

	const handleResquestTypeChange = (event: SelectChangeEvent) => {
		setRequestType(event.target.value as string);
	};

	const handleMethodChange = (event: SelectChangeEvent) => {
		setMethod(event.target.value as string);
	};

	const handleResponseTypeChange = (event: SelectChangeEvent) => {
		setResponseType(event.target.value as string);
	};

	const handleAuthTypeChange = (event: SelectChangeEvent) => {
		setAuthType(event.target.value as string);
	};

	// Parameters function
	async function getDescriptionParameters() {
		await api.get("api/list_description_api/").then((response: any) => {
			setListParameterDescription(response.content.results);
		});
	}

	const handleLocalParameterChange = (event: SelectChangeEvent) => {
		setLocalParameter(event.target.value as string);
	};

	const handleFixedParameterChange = (event: SelectChangeEvent) => {
		setFixedParameter(event.target.value as string);
		setFieldValue("parameter_value", "");
	};

	const handleValueTypeParameterChange = (event: SelectChangeEvent) => {
		setValueTypeParameter(
			event.target.value as "1" | "2" | "3" | "4" | "5" | "6",
		);
		if (["3", "4", "5", "6"].indexOf(event.target.value) > -1) {
			if (event.target.value === "3") {
				setRequired("0");
			} else {
				setFieldValue("boolean_default", "");
				setFieldValue("boolean_value", "");
			}
			setFixedParameter("0");
			setFieldValue("parameter_value", "");
		}
	};

	const handleListApiIdChange = (event: SelectChangeEvent) => {
		const id = event.target.value as string;
		setFieldValue("list_api_id", id);

		// sugere group_key automaticamente (não obriga)
		if (!values.group_key?.trim()) {
			setFieldValue("group_key", makeGroupKeyFromListApiId(id));
		}

		// se já existe outro parâmetro com a mesma list_api_id, faz sentido habilitar agrupamento
		const alreadyUsed = parameters.some((p) => p.list_api_id === id);
		if (!alreadyUsed) {
			setFieldValue("group_enabled", "0");
			setFieldValue("group_label", "");
		}
	};

	const handleUiPositionChange = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const n = Number(event.target.value || 1);
		setFieldValue("ui_position", Number.isFinite(n) && n > 0 ? n : 1);
	};

	const handleRequiredChange = (event: SelectChangeEvent) => {
		setRequired(event.target.value as string);
	};

	const normalizeParamKey = (name: string) => name.replace(/[{}]/g, "").trim();

	const makeGroupKeyFromListApiId = (listApiId: string) => {
		// "reference", "models", etc. aqui vamos manter simples e determinístico
		return `list_${listApiId.replace(/-/g, "").slice(0, 8)}`;
	};

	const getGroupedFieldsForListApi = (
		listApiId: string,
		currentParamName: string,
	) => {
		const names = parameters
			.filter((p) => p.list_api_id === listApiId)
			.map((p) => normalizeParamKey(p.name));

		const current = normalizeParamKey(currentParamName);
		if (!names.includes(current)) names.push(current);

		return Array.from(new Set(names));
	};

	const isPlateDraft = /placa/i.test(
		`${values.parameter_name} ${values.parameter_description}`,
	);

	// Page functions
	const addParameter = async () => {
		if (values.parameter_name.length === 0) {
			return toast.error("CUIDADO! Não adicione parâmetros vazios!", {
				position: toast.POSITION.TOP_RIGHT,
			});
		}

		if (values.parameter_name.includes(" ")) {
			return toast.error("CUIDADO! Não adicione espaços no campo parâmetro!", {
				position: toast.POSITION.TOP_RIGHT,
			});
		}

		if (values.parameter_description.length === 0) {
			return toast.error(
				"ATENÇÃO! Você precisa adicionar uma descrição para seu cliente.",
				{
					position: toast.POSITION.TOP_RIGHT,
				},
			);
		}

		if (["1", "2"].indexOf(valueTypeParameter) > -1) {
			if (fixedParameter === "1" && values.parameter_value.length === 0) {
				return toast.error(
					"CUIDADO! Você precisa adicionar um valor válido para parâmetros fixados! (De acordo com tipo de valor escolhido)",
					{
						position: toast.POSITION.TOP_RIGHT,
					},
				);
			}
		}

		if (valueTypeParameter === "3") {
			if (values.boolean_default.length === 0) {
				return toast.error(
					"CUIDADO! Você precisa adicionar uma valor padrão para parâmetros do tipo booleano!",
					{
						position: toast.POSITION.TOP_RIGHT,
					},
				);
			}

			if (values.boolean_value.length === 0) {
				return toast.error(
					"CUIDADO! Você precisa adicionar uma valor se marcado para parâmetros do tipo booleano!",
					{
						position: toast.POSITION.TOP_RIGHT,
					},
				);
			}
		}

		const withMercosulRetry = isPlateDraft && newMercosulRetry.enabled;

		if (withMercosulRetry && newMercosulRetry.when.length === 0) {
			return toast.error(
				"Adicione pelo menos uma condição para tentar a placa no padrão Mercosul.",
				{
					position: toast.POSITION.TOP_RIGHT,
				},
			);
		}

		let exist = false;

		parameters.forEach((item) => {
			if (item.name === values.parameter_name) {
				exist = true;
			}
		});

		if (exist) {
			return toast.error("CUIDADO! Esse parâmetro já foi adicionado!", {
				position: toast.POSITION.TOP_RIGHT,
			});
		}

		if (listParameterDescription.length > 0) {
			let new_parameters_description = listParameterDescription.filter(
				(item) => item.name === values.parameter_description,
			);

			if (new_parameters_description.length === 0) {
				await createParameterDescription({
					name: values.parameter_description,
				});
			}
		} else {
			await createParameterDescription({ name: values.parameter_description });
		}

		const paramName = values.parameter_name.replaceAll(" ", "-");

		const useListApi = values.use_list_api === "1";
		const uiDependsOn = (values.ui_depends_on || [])
			.map(normalizeParamKey)
			.filter(Boolean);

		if (values.use_list_api === "1" && !values.list_api_id) {
			return toast.error("Selecione a API de lista para este parâmetro.", {
				position: toast.POSITION.TOP_RIGHT,
			});
		}

		// @ts-ignore
		setParameters((prev) => {
			const listApiId = values.list_api_id;

			const isList = useListApi;
			const shouldGroup =
				isList &&
				values.group_enabled === "1" &&
				!!listApiId &&
				prev.some((p) => p.list_api_id === listApiId);

			//se marcou agrupar mas não existe par, bloqueia
			if (
				isList &&
				values.group_enabled === "1" &&
				!prev.some((p) => p.list_api_id === listApiId)
			) {
				toast.error(
					"Para agrupar, primeiro adicione outro parâmetro usando a mesma API de lista.",
					{
						position: toast.POSITION.TOP_RIGHT,
					},
				);
				return prev; // não adiciona
			}

			const base: any = {
				name: paramName,
				description: values.parameter_description,
				local: localParameter,
				fixed: fixedParameter === "1",
				value_type: valueTypeParameter,
				value: values.parameter_value,
				required: required === "1",
				boolean_default: values.boolean_default,
				boolean_value: values.boolean_value,
				...(withMercosulRetry ? { mercosul_retry: newMercosulRetry } : {}),
			};

			if (!isList) {
				return [...prev, base];
			}

			const baseUi: any = {
				position: Number(values.ui_position || 1),
				component: "select",
				depends_on: uiDependsOn,
			};

			if (shouldGroup) {
				const groupKey =
					values.group_key?.trim() || makeGroupKeyFromListApiId(listApiId);
				const groupFields = getGroupedFieldsForListApi(listApiId, paramName);
				const groupLabel = values.group_label?.trim() || "Seleção";

				baseUi.group_key = groupKey;
				baseUi.group_fields = groupFields;
				baseUi.group_label = groupLabel;

				// sincroniza os parâmetros antigos do mesmo list_api_id
				const syncedPrev = prev.map((p: any) => {
					if (p.list_api_id !== listApiId) return p;

					// garante ui
					const pUi = p.ui || { component: "select" };

					return {
						...p,
						ui: {
							...pUi,
							group_key: groupKey,
							group_fields: groupFields,
							group_label: groupLabel,
						},
					};
				});

				return [
					...syncedPrev,
					{
						...base,
						list_api_id: listApiId,
						ui: baseUi,
					},
				];
			}

			// sem grupo
			return [
				...prev,
				{
					...base,
					list_api_id: listApiId,
					ui: baseUi,
				},
			];
		});
		setFieldValue("parameter_name", "");
		setFieldValue("parameter_description", "");
		// setLocalParameter("1")
		// setRequired("1")
		setValueTypeParameter("1");
		setFixedParameter("0");
		setFieldValue("parameter_value", "");
		setFieldValue("boolean_default", "");
		setFieldValue("boolean_value", "");
		setNewMercosulRetry({ enabled: false, when: [] });

		// reset list-ui
		setFieldValue("use_list_api", "0");
		setFieldValue("list_api_id", "");
		setFieldValue("ui_position", 1);
		setFieldValue("ui_depends_on", []);
		setFieldValue("group_enabled", "0");
		setFieldValue("group_label", "");
		setFieldValue("group_key", "");
	};

	async function createParameterDescription(data: LooseObject) {
		await api
			.post("api/create_description_api/", data)
			.then(async (response: any) => await getDescriptionParameters());
	}

	const removeParameter = (parameter_name: string) => {
		setParameters((prev) => {
			const next = prev.filter((p) => p.name !== parameter_name);

			// reprocessa agrupamentos por list_api_id
			const byListApi: Record<string, any[]> = {};

			next.forEach((p: any) => {
				if (!p.list_api_id) return;
				byListApi[p.list_api_id] = byListApi[p.list_api_id] || [];
				byListApi[p.list_api_id].push(p);
			});

			const rebuilt = next.map((p: any) => {
				if (!p.list_api_id) return p;

				const group = byListApi[p.list_api_id] || [];
				const hasGroupKey = !!p.ui?.group_key;

				// se não tinha grupo, não mexe
				if (!hasGroupKey) return p;

				// se sobrou 0/1, desfaz grupo
				if (group.length <= 1) {
					const ui = { ...(p.ui || {}) };
					delete ui.group_key;
					delete ui.group_label;
					delete ui.group_fields;
					return { ...p, ui };
				}

				// se sobrou 2+, recalcula group_fields
				const groupKey = p.ui.group_key;
				const groupLabel = p.ui.group_label || "Seleção";
				const groupFields = Array.from(
					new Set(group.map((x) => normalizeParamKey(x.name))),
				);

				const ui = {
					...(p.ui || {}),
					group_key: groupKey,
					group_label: groupLabel,
					group_fields: groupFields,
				};

				return { ...p, ui };
			});

			return rebuilt;
		});
	};

	const dysplayRequired = (value: boolean | undefined) => {
		return value ? "Obrigatório" : "Não obrigatório";
	};

	const displayFixed = (value: boolean | undefined) => {
		return value ? "Valor fixo" : "Valor não fixo";
	};

	async function createOrUpdateApi() {
		if (values.cache_enabled) {
			const ttl = Number(values.cache_ttl_days);
			if (!Number.isFinite(ttl) || ttl <= 0) {
				toast.error(
					"Para ativar o cache, informe um número maior que 0 em 'Tempo de cache (dias)'.",
					{ position: toast.POSITION.TOP_RIGHT },
				);
				return; // impede o submit
			}
		}

		const timeout = Number(values.timeout_seconds);
		if (!Number.isFinite(timeout) || timeout <= 0) {
			toast.error("Informe um timeout maior que 0 segundos.", {
				position: toast.POSITION.TOP_RIGHT,
			});
			return;
		}

		const failureRule =
			values.failure_rule.length > 0 ? values.failure_rule : null;

		if (category === "4" && parameters.length === 0) {
			toast.error(
				"CUIDADO! Adicione ao menos uma credencial (header) para a Api de geração de certificado!",
				{
					position: toast.POSITION.TOP_RIGHT,
				},
			);
			return;
		}

		setParametersError(null);

		const data = {
			category: category,
			name: values.name,
			url: values.url,
			description: values.description,
			request_type: requestType,
			method: method,
			response_type: responseType,
			auth_type: authType,
			dynamic_token: dynamicToken,
			dynamic_token_id: values.dynamic_token_id,
			parameters: parameters,
			structure: values.jsonstructure,
			webhook: values.webhook,
			cache_enabled: values.cache_enabled,
			cache_ttl_days: values.cache_ttl_days,
			cache_required_response_paths: values.cache_required_response_paths,
			timeout_seconds: Number(values.timeout_seconds),
			failure_rule: failureRule,
		};

		if (id) {
			toast.loading("Atualizando...", {
				position: toast.POSITION.TOP_RIGHT,
			});

			var resUpdated = (await api.put(`api/update/${id}/`, data)) as {
				[key: string]: any;
			};

			toast.dismiss();

			if (resUpdated.status === 200) {
				toast.success("Dados atualizados com sucesso!", {
					position: toast.POSITION.TOP_RIGHT,
				});
			} else {
				showSaveError(
					resUpdated,
					"Ops... Tivemos um problema, por favor tente novamente mais tarde!",
				);
			}
		} else {
			const newData = { ...data, provider: provider_id };

			toast.loading("Criando...", {
				position: toast.POSITION.TOP_RIGHT,
			});

			var resCreated = (await api.post("api/create/", newData)) as {
				[key: string]: any;
			};

			toast.dismiss();

			if (resCreated.status === 201) {
				setFieldValue("name", "");
				setFieldValue("url", "");
				setFieldValue("description", "");
				setRequestType("1");
				setMethod("1");
				setResponseType("1");
				setAuthType("1");
				setDynamicToken(false);
				setApiListOptions([]);
				setCategory("1");
				setParameters([]);
				toast.success("API cadastrada com sucesso!", {
					position: toast.POSITION.TOP_RIGHT,
				});
			} else {
				showSaveError(
					resCreated,
					"Ops.. Tivemos um problema, por favor tente novamente mais tarde!",
				);
			}
		}
	}

	function showSaveError(response: { data?: unknown }, fallback: string) {
		const parametersError = extractParametersError(response);
		if (parametersError) setParametersError(parametersError);
		toast.error(parametersError ?? fallback, {
			position: toast.POSITION.TOP_RIGHT,
		});
	}

	function ModalInfo({ open, item }: ModalInfoProps) {
		return (
			<Modal
				open={open}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
			>
				<Box
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						width: "90%",
						bgcolor: "background.paper",
						boxShadow: 24,
						p: 4,
					}}
				>
					<Typography
						id="modal-modal-title"
						variant="h5"
						component="h2"
						textAlign="center"
					>
						DADOS DO PARAMETRO:{" "}
						<strong style={{ color: palette.info.main }}>{item?.name}</strong>
					</Typography>
					<ul>
						<li>
							<strong>Descrição:</strong> {item?.description}
						</li>
						<li>
							<strong>Onde o parâmetro será enviado:</strong>{" "}
							{dysplayLocal[item?.local as keyof typeof dysplayLocal]}
						</li>
						<li>
							<strong>Parâmetro obrigatório:</strong>{" "}
							{dysplayRequired(item?.required)}
						</li>
						<li>
							<strong>Tipo de valor:</strong>{" "}
							{
								dysplayValueType[
									item?.value_type as keyof typeof dysplayValueType
								]
							}
						</li>
						<li>
							<strong>Valor fixado:</strong> {displayFixed(item?.fixed)}
						</li>

						{item?.fixed ? (
							<li>
								<strong>Valor:</strong> {item?.value}
							</li>
						) : null}

						{item?.mercosul_retry ? (
							<li>
								<strong>Tentar placa no padrão Mercosul:</strong>{" "}
								{item.mercosul_retry.enabled ? "Sim" : "Não"}
								{item.mercosul_retry.enabled && (
									<ul>
										{item.mercosul_retry.when.map((rule) => (
											<li key={JSON.stringify(rule)}>{describeMatch(rule)}</li>
										))}
									</ul>
								)}
							</li>
						) : null}

						{item?.value_type === "3" ? (
							<>
								<li>
									<strong>Valor padrão:</strong> {item?.boolean_default}
								</li>
								<li>
									<strong>Valor se marcado:</strong> {item?.boolean_value}
								</li>
							</>
						) : null}

						{item?.list_api_id ? (
							<>
								<hr />
								<li>
									<strong>Usa API de lista:</strong> Sim
								</li>
								<li>
									<strong>List API ID:</strong> {item.list_api_id}
								</li>

								{item?.ui ? (
									<>
										<li>
											<strong>UI Componente:</strong>{" "}
											{item.ui.component || "select"}
										</li>
										<li>
											<strong>UI Posição:</strong> {item.ui.position ?? "-"}
										</li>
										<li>
											<strong>UI Dependências:</strong>{" "}
											{Array.isArray(item.ui.depends_on) &&
											item.ui.depends_on.length
												? item.ui.depends_on.join(", ")
												: "-"}
										</li>

										{item.ui.group_key ? (
											<>
												<li>
													<strong>Agrupamento:</strong> Sim
												</li>
												<li>
													<strong>Label do Agrupamento:</strong>{" "}
													{item.ui.group_label || "-"}
												</li>
												<li>
													<strong>Campos do Agrupamento:</strong>{" "}
													{Array.isArray(item.ui.group_fields) &&
													item.ui.group_fields.length
														? item.ui.group_fields.join(", ")
														: "-"}
												</li>
											</>
										) : (
											<li>
												<strong>Agrupamento:</strong> Não
											</li>
										)}
									</>
								) : (
									<li>
										<strong>UI:</strong> -
									</li>
								)}
							</>
						) : (
							<li>
								<strong>Usa API de lista:</strong> Não
							</li>
						)}
					</ul>

					<Grid
						container
						gap={3}
						marginTop={3}
						alignItems="center"
						justifyContent="center"
					>
						<Button
							variant="outlined"
							size="large"
							color="info"
							onClick={() => setOpenInfo(-1)}
						>
							Fechar
						</Button>
					</Grid>
				</Box>
			</Modal>
		);
	}

	const getTokensApis = useCallback(async () => {
		if (urlTokenApis) {
			await api.get(urlTokenApis).then((response: any) => {
				setDynamicsTokens(response.content.results);
			});
		}
	}, [urlTokenApis]);

	const handleChangeDynamicToken = () => {
		setDynamicToken(!dynamicToken);
		if (!dynamicToken) getTokensApis();
	};

	const getListApis = useCallback(async () => {
		if (urlListApis) {
			await api.get(urlListApis).then((response: any) => {
				setApiListOptions(response.content.results);
			});
		}
	}, [urlListApis]);

	const handleChangeListApi = (event: SelectChangeEvent) => {
		const v = event.target.value as "0" | "1";
		setFieldValue("use_list_api", v);
		if (v === "1") {
			getListApis();
		}

		// reset quando desmarca
		if (v === "0") {
			setFieldValue("list_api_id", "");
			setFieldValue("ui_position", 1);
			setFieldValue("ui_depends_on", []);
			setFieldValue("group_enabled", "0");
			setFieldValue("group_label", "");
			setFieldValue("group_key", "");
		}
	};

	useEffect(() => {
		const getApi = async () => {
			getTokensApis();
			getListApis();

			const response = (await api.get(
				`api/retrieve/${id}/`,
			)) as RetrieveApiProps;

			setFieldValue("name", response.content.name);
			setFieldValue("url", response.content.url);
			setFieldValue("description", response.content.description);
			setFieldValue("webhook", response.content.webhook);
			setRequestType(response.content.request_type);
			setMethod(response.content.method);
			setResponseType(response.content.response_type);
			setAuthType(response.content.auth_type);
			setDynamicToken(response.content.dynamic_token);
			setFieldValue("dynamic_token_id", response.content.dynamic_token_id);
			setCategory(response.content.category);
			setParameters(response.content.parameters);
			setUrlTokenApis(`api/list/${response.content.provider}/?category=2`);
			setUrlListApis(`api/list/${response.content.provider}/?category=3`);
			setFieldValue("jsonstructure", response.content.structure);
			setFieldValue("cache_enabled", response.content.cache_enabled);
			setFieldValue("cache_ttl_days", response.content.cache_ttl_days);
			setFieldValue(
				"cache_required_response_paths",
				response.content.cache_required_response_paths,
			);
			setFieldValue("timeout_seconds", response.content.timeout_seconds ?? 180);
			setFieldValue("failure_rule", response.content.failure_rule ?? []);
		};

		if (id) {
			setTitleBar(`API - ${values.name} (${location.state.fantasy_name})`);
			getApi().catch(console.error);
		} else {
			setTitleBar(`Nova API para o fornecedor ${location.state.fantasy_name}`);
			setUrlTokenApis(`api/list/${provider_id}/?category=2`);
			setUrlListApis(`api/list/${provider_id}/?category=3`);
		}

		getDescriptionParameters();
	}, [id, setFieldValue, provider_id, getTokensApis]);

	useLayoutEffect(() => {
		setPathTitleBar(undefined);
	}, [setPathTitleBar]);

	return (
		<>
			<ToastContainer />
			<ModalInfo open={openInfo !== -1} item={parameters[openInfo]} />
			<form onSubmit={handleSubmit}>
				<Grid
					container
					spacing={2}
					direction="row"
					justifyContent="space-evenly"
					alignItems="center"
				>
					<Grid item md={12} xs={12}>
						INFORMAÇÕES GERAIS
					</Grid>
					<Grid item md={4} xs={12}>
						<FormControl fullWidth>
							<InputLabel id="category-label">Categoria</InputLabel>
							<Select
								fullWidth
								required={true}
								labelId="category-label"
								id="category"
								name="category"
								value={category}
								label="Categoria"
								onChange={handleCategoryChange}
							>
								{Categories?.map((item) => (
									<MenuItem key={`category-${item.id}`} value={item.id}>
										{item.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item md={4} xs={12}>
						<TextField
							fullWidth
							required={true}
							id="name"
							name="name"
							label="Nome"
							value={values.name}
							onChange={handleChange}
							error={touched.name && Boolean(errors.name)}
							helperText={touched.name && errors.name}
						/>
					</Grid>
					<Grid item md={4} xs={12}>
						<TextField
							fullWidth
							required={true}
							id="url"
							name="url"
							label="URL"
							value={values.url}
							onChange={handleChange}
							error={touched.url && Boolean(errors.url)}
							helperText={touched.url && errors.url}
						/>
					</Grid>
					<Grid item md={12} xs={12}>
						<TextField
							fullWidth
							multiline
							rows={4}
							id="description"
							name="description"
							label="Descrição"
							value={values.description}
							onChange={handleChange}
							error={touched.description && Boolean(errors.description)}
							helperText={touched.description && errors.description}
						/>
					</Grid>
					<Grid item md={4} xs={12}>
						<FormControl fullWidth>
							<InputLabel id="request_type-label">
								Tipo de requisição
							</InputLabel>
							<Select
								fullWidth
								required={true}
								labelId="request_type-label"
								id="request_type"
								name="request_type"
								value={requestType}
								label="Tipo de requisição"
								onChange={handleResquestTypeChange}
							>
								{RequestTypes?.map((item) => (
									<MenuItem key={`request-type-${item.id}`} value={item.id}>
										{item.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item md={4} xs={12}>
						<FormControl fullWidth>
							<InputLabel id="method-label">Método</InputLabel>
							<Select
								fullWidth
								required={true}
								labelId="method-label"
								id="method"
								name="method"
								value={method}
								label="Método"
								onChange={handleMethodChange}
							>
								{Methods?.map((item) => (
									<MenuItem key={`method-${item.id}`} value={item.id}>
										{item.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item md={4} xs={12}>
						<FormControl fullWidth>
							<InputLabel id="response_type-label">Tipo de resposta</InputLabel>
							<Select
								fullWidth
								required={true}
								labelId="response_type-label"
								id="response_type"
								name="response_type"
								value={responseType}
								label="Tipo de resposta"
								onChange={handleResponseTypeChange}
							>
								{ResponseTypes?.map((item) => (
									<MenuItem key={`response-type-${item.id}`} value={item.id}>
										{item.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item md={4} xs={12}>
						<FormControl fullWidth>
							<InputLabel id="auth_type-label">Tipo de autenticação</InputLabel>
							<Select
								fullWidth
								required={true}
								labelId="auth_type-label"
								id="auth_type"
								name="auth_type"
								value={authType}
								label="Tipo de autenticação"
								onChange={handleAuthTypeChange}
							>
								{AuthTypes?.map((item) => (
									<MenuItem key={`auth-type-${item.id}`} value={item.id}>
										{item.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					{/* Token dinamico */}
					{category === "1" || category === "3" ? (
						<Grid item md={12} xs={12}>
							<Alert
								color="info"
								severity="info"
								variant="outlined"
								sx={{ display: "flex", alignItems: "center" }}
							>
								<FormGroup>
									<FormControlLabel
										control={
											<Switch
												color="info"
												checked={dynamicToken}
												onChange={handleChangeDynamicToken}
											/>
										}
										label="Ative essa opção caso haja a necessidade de utilizar tokens dinâmicos nessa integração"
									/>
								</FormGroup>
								<Typography variant="body2" paragraph>
									Dessa forma, você primeiro deve cadastrar a integração para se
									obter tokens de forma dinâmica e então ativa essa opção e
									vincula com a integração.
								</Typography>
								{dynamicToken ? (
									<FormControl fullWidth>
										<InputLabel id="dynamic_token_id-label">
											Selecione uma API de token dinâmico
										</InputLabel>
										<Select
											fullWidth
											required={true}
											labelId="dynamic_token_id-label"
											id="dynamic_token_id"
											name="dynamic_token_id"
											value={values.dynamic_token_id}
											label="Selecione uma API de token dinâmico"
											onChange={handleChange}
										>
											{dynamicsTokens?.map((item) => (
												<MenuItem
													key={`dynamic_token_id-${item.id}`}
													value={item.id}
												>
													{item.name}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								) : (
									<></>
								)}
							</Alert>
						</Grid>
					) : (
						<></>
					)}
					<Grid item md={12} xs={12}>
						<Alert
							color="info"
							severity="info"
							variant="outlined"
							style={{ alignItems: "center" }}
						>
							<FormGroup>
								<FormControlLabel
									control={
										<Switch
											color="info"
											checked={values.webhook}
											onChange={() => setFieldValue("webhook", !values.webhook)}
										/>
									}
									label="Ative essa opção caso a API funcione com webhook"
								/>
							</FormGroup>
						</Alert>
					</Grid>
					<Grid item md={4} xs={12}>
						<TextField
							fullWidth
							required={true}
							id="timeout_seconds"
							name="timeout_seconds"
							type="number"
							label="Timeout (segundos)"
							value={values.timeout_seconds}
							onChange={handleChange}
							inputProps={{ min: 1 }}
							helperText="Tempo máximo de espera pela resposta do fornecedor (padrão: 180s)"
						/>
					</Grid>
					{/* Variaveis */}
					<Grid item md={12} xs={12}>
						PARÂMETROS
					</Grid>
					<Grid item md={12} xs={12}>
						<Alert severity="info">
							Parâmetros necessários para funcionamento da API
						</Alert>
					</Grid>
					{parametersError && (
						<Grid item md={12} xs={12}>
							<Alert severity="error">{parametersError}</Alert>
						</Grid>
					)}
					<Grid item md={12} xs={12} alignItems="center" alignContent="center">
						<List dense disablePadding sx={{ width: "100%" }}>
							{parameters?.map((item, index) => (
								<ListItem key={item.name} disablePadding>
									<ListItemAvatar>
										<Avatar>
											<AutoAwesomeIcon />
										</Avatar>
									</ListItemAvatar>
									<ListItemButton
										onClick={() => {
											setOpenInfo(index);
										}}
									>
										<ListItemText
											primary={item.description}
											secondary={item.name}
										/>
									</ListItemButton>
									<ListItemSecondaryAction>
										<IconButton
											edge="end"
											aria-label="delete"
											onClick={() => {
												removeParameter(item.name);
											}}
										>
											<DeleteIcon />
										</IconButton>
									</ListItemSecondaryAction>
								</ListItem>
							))}
						</List>
					</Grid>
					<Grid item md={4} xs={12}>
						<TextField
							fullWidth
							id="parameter_name"
							name="parameter_name"
							label="Parâmetro"
							value={values.parameter_name}
							onChange={handleChange}
							helperText="Exatamente conforme documentação do fornecedor"
						/>
					</Grid>
					<Grid item md={4} xs={12}>
						<Autocomplete
							id="parameter_description"
							freeSolo
							options={
								listParameterDescription.length > 0
									? listParameterDescription.map((item) => item.name)
									: []
							}
							onChange={(e: any) => {
								setFieldValue("parameter_description", e.target.textContent);
							}}
							renderInput={(params) => (
								<TextField
									{...params}
									fullWidth
									name="parameter_description"
									label="Descrição"
									value={values.parameter_description}
									onChange={handleChange}
									helperText="Texto que será exibido para o seu cliente"
								/>
							)}
						/>
					</Grid>
					<Grid item md={4} xs={12}>
						<FormControl fullWidth>
							<InputLabel id="local_parameter-label">
								Onde o parâmetro será enviado
							</InputLabel>
							<Select
								fullWidth
								labelId="local_parameter-label"
								id="local_parameter"
								name="local_parameter"
								value={localParameter}
								label="Onde o parâmetro será enviado"
								onChange={handleLocalParameterChange}
							>
								{LocalParameter?.map((item) => (
									<MenuItem key={`local-parameter-${item.id}`} value={item.id}>
										{item.name}
									</MenuItem>
								))}
							</Select>
							<FormHelperText>
								Conforme documentação do fornecedor
							</FormHelperText>
						</FormControl>
					</Grid>
					<Grid item md={6} xs={12}>
						<FormControl fullWidth>
							<InputLabel id="required-label">
								Parâmetro obrigatório?
							</InputLabel>
							<Select
								fullWidth
								labelId="required-label"
								disabled={valueTypeParameter === "3"}
								id="required"
								name="required"
								value={required}
								label="Parâmetro obrigatório?"
								onChange={handleRequiredChange}
							>
								<MenuItem value="1">Sim</MenuItem>
								<MenuItem value="0">Não</MenuItem>
							</Select>
							<FormHelperText>
								Se o campo não for preenchido pelo seu cliente,{" "}
								<strong>NÃO</strong> será possível realizar a chamada
							</FormHelperText>
						</FormControl>
					</Grid>
					<Grid item md={6} xs={12}>
						<FormControl fullWidth>
							<InputLabel id="value_type_parameter-label">
								Tipo de valor
							</InputLabel>
							<Select
								fullWidth
								labelId="value_type_parameter-label"
								id="value_type_parameter"
								name="value_type_parameter"
								value={valueTypeParameter}
								label="Tipo de valor"
								onChange={handleValueTypeParameterChange}
							>
								{ValuesTypes?.map((item) => (
									<MenuItem key={`value-type-${item.id}`} value={item.id}>
										{item.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormHelperText>
							Tipo de informação que seu cliente deve informar (Seguir
							documentação do fornecedor)
						</FormHelperText>
					</Grid>
					{["1", "2"].indexOf(valueTypeParameter) > -1 ? (
						<>
							<Grid item md={fixedParameter === "1" ? 6 : 12} xs={12}>
								<FormControl fullWidth>
									<InputLabel id="fixed_parameter-label">
										Valor fixado?
									</InputLabel>
									<Select
										fullWidth
										labelId="fixed_parameter-label"
										id="fixed_parameter"
										name="fixed_parameter"
										value={fixedParameter}
										label="Valor fixado?"
										onChange={handleFixedParameterChange}
									>
										<MenuItem value="0">Não</MenuItem>
										<MenuItem value="1">Sim</MenuItem>
									</Select>
									<FormHelperText>
										Se o valor for fixado, seu cliente <strong>NÃO</strong>{" "}
										precisará preencher esse campo
									</FormHelperText>
								</FormControl>
							</Grid>
							{fixedParameter === "1" ? (
								<Grid item md={6} xs={12}>
									<TextField
										fullWidth
										helperText="Você precisa dicionar um valor para parâmetros fixos"
										type={valueTypeParameter === "2" ? "number" : "text"}
										id="parameter_value"
										name="parameter_value"
										label="Informe o valor fixado"
										value={values.parameter_value}
										onChange={handleChange}
									/>
								</Grid>
							) : (
								<></>
							)}
						</>
					) : (
						<></>
					)}
					{valueTypeParameter === "3" ? (
						<>
							<Grid item md={6} xs={12}>
								<TextField
									fullWidth
									id="boolean_default"
									name="boolean_default"
									label="Valor padrão"
									value={values.boolean_default}
									onChange={handleChange}
									helperText="Um campo do tipo checkbox é exibido ao cliente e caso ele NÃO marque a opção, esse será o valor enviado ao fornecedor"
								/>
							</Grid>
							<Grid item md={6} xs={12}>
								<TextField
									fullWidth
									id="boolean_value"
									name="boolean_value"
									label="Valor se marcado"
									value={values.boolean_value}
									onChange={handleChange}
									helperText="Um campo do tipo checkbox é exibido ao cliente e caso ele marque a opção, esse será o valor enviado ao fornecedor"
								/>
							</Grid>
						</>
					) : (
						<></>
					)}

					{/* LIST API (por parâmetro) */}
					<Grid item md={6} xs={12}>
						<FormControl fullWidth>
							<InputLabel id="use_list_api">
								Parâmetro utiliza uma API de lista?
							</InputLabel>
							<Select
								fullWidth
								labelId="use_list_api"
								id="use_list_api"
								name="use_list_api"
								value={values.use_list_api}
								label="Parâmetro utiliza uma API de lista?"
								onChange={handleChangeListApi}
							>
								<MenuItem value="0">Não</MenuItem>
								<MenuItem value="1">Sim</MenuItem>
							</Select>
						</FormControl>
					</Grid>

					{isPlateDraft && (
						<Grid item md={6} xs={12}>
							<FormControlLabel
								control={
									<Checkbox
										checked={newMercosulRetry.enabled}
										onChange={(e) =>
											setNewMercosulRetry((prev) => ({
												...prev,
												enabled: e.target.checked,
											}))
										}
									/>
								}
								label="Tentar placa no padrão Mercosul"
							/>
							<FormHelperText>
								Se o fornecedor não encontrar a placa antiga, tenta de novo no
								padrão Mercosul (ABC1234 → ABC1C34).
							</FormHelperText>
						</Grid>
					)}

					{isPlateDraft && newMercosulRetry.enabled && (
						<Grid
							item
							md={12}
							xs={12}
							component="section"
							aria-label="Condições de placa não encontrada"
						>
							<Alert severity="info" sx={{ marginBottom: 2 }}>
								Diga como reconhecer a resposta de placa não encontrada.
							</Alert>
							<FailureConditionsBuilder
								rules={newMercosulRetry.when}
								onChange={(when) =>
									setNewMercosulRetry((prev) => ({ ...prev, when }))
								}
								allowRequery={false}
							/>
						</Grid>
					)}

					{values.use_list_api === "1" && (
						<>
							<Grid item md={6} xs={12}>
								<FormControl fullWidth>
									<InputLabel id="list_api_id_label">
										Selecione a API de lista (do mesmo fornecedor)
									</InputLabel>
									<Select
										fullWidth
										labelId="list_api_id_label"
										id="list_api_id"
										name="list_api_id"
										value={values.list_api_id}
										label="Selecione a API de lista"
										onChange={handleListApiIdChange}
									>
										{apiListOptions.length > 0 ? (
											apiListOptions.map((item) => (
												<MenuItem key={item.id} value={item.id}>
													{item.name}
												</MenuItem>
											))
										) : (
											<MenuItem disabled value="">
												Nenhuma API de lista disponível
											</MenuItem>
										)}
									</Select>
								</FormControl>
							</Grid>

							<Grid item md={6} xs={12}>
								<TextField
									fullWidth
									type="number"
									label="Posição do select (ordem na UI)"
									value={values.ui_position}
									onChange={handleUiPositionChange}
									inputProps={{ min: 1 }}
									helperText="Ex.: 1 = primeiro select, 2 = segundo..."
								/>
							</Grid>

							<Grid item md={12} xs={12}>
								<Autocomplete
									multiple
									freeSolo
									options={parameters
										.map((p) => normalizeParamKey(p.name))
										.filter(Boolean)}
									value={values.ui_depends_on}
									onChange={(_, newValue) =>
										setFieldValue("ui_depends_on", newValue)
									}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Dependências (campos que precisam estar preenchidos antes)"
											helperText="Ex.: vehicle_type, year_reference, month_reference..."
										/>
									)}
								/>
							</Grid>
							<Grid item md={6} xs={12}>
								<FormControl fullWidth>
									<InputLabel id="group_enabled">
										Agrupar parâmetros deste select?
									</InputLabel>
									<Select
										fullWidth
										labelId="group_enabled"
										id="group_enabled"
										name="group_enabled"
										value={values.group_enabled}
										label="Agrupar parâmetros deste select?"
										onChange={handleChange}
									>
										<MenuItem value="0">Não</MenuItem>
										<MenuItem
											value="1"
											disabled={
												!values.list_api_id ||
												!parameters.some(
													(p) => p.list_api_id === values.list_api_id,
												)
											}
										>
											Sim
										</MenuItem>
									</Select>
									<FormHelperText>
										Só faz sentido quando dois parâmetros compartilham a mesma
										API de lista.
									</FormHelperText>
								</FormControl>
							</Grid>

							{values.group_enabled === "1" && (
								<Grid item md={6} xs={12}>
									<TextField
										fullWidth
										id="group_label"
										name="group_label"
										label="Título do grupo (label)"
										value={values.group_label}
										onChange={handleChange}
										helperText="Ex.: Referência (mês/ano)"
									/>
								</Grid>
							)}
						</>
					)}
				</Grid>
				<Grid
					container
					spacing={2}
					direction="column"
					alignItems="center"
					justifyContent="space-evenly"
				>
					<Grid
						item
						md={12}
						xs={12}
						alignItems="center"
						alignContent="center"
						marginTop={5}
						marginBottom={5}
					>
						<Button
							variant="contained"
							size="medium"
							color="primary"
							onClick={addParameter}
						>
							Adicionar
						</Button>
					</Grid>

					<Grid item md={12} xs={12} style={{ width: "100%" }}>
						<Grid item md={12} xs={12}>
							ESTRUTURA JSON
						</Grid>

						<Grid item md={12} xs={12}>
							<br />
							<JsonCodeEditor
								id="jsonstructure"
								label="JSON"
								value={values.jsonstructure}
								onChange={(value) => setFieldValue("jsonstructure", value)}
								error={touched.description && Boolean(errors.jsonstructure)}
								helperText={
									touched.jsonstructure ? errors.jsonstructure : undefined
								}
								minHeight="100px"
							/>
						</Grid>
					</Grid>
					<Grid item md={12} xs={12} style={{ width: "100%" }}>
						<Grid item md={12} xs={12} style={{ marginTop: 32 }}>
							CONFIGURAÇÕES DE CACHE
						</Grid>
						<br />
						<Grid container spacing={2}>
							<Grid item md={12} xs={12}>
								<Alert
									color="info"
									severity="info"
									variant="outlined"
									sx={{ display: "flex", alignItems: "center" }}
								>
									<FormGroup>
										<FormControlLabel
											control={
												<Switch
													color="info"
													checked={values.cache_enabled}
													onChange={() =>
														setFieldValue(
															"cache_enabled",
															!values.cache_enabled,
														)
													}
												/>
											}
											label="Ativar cache desta API (reutiliza consultas anteriores com mesmos inputs)"
										/>
									</FormGroup>
								</Alert>
							</Grid>
							{values.cache_enabled && (
								<>
									<Grid item md={6} xs={12}>
										<TextField
											fullWidth
											id="cache_ttl_days"
											name="cache_ttl_days"
											type="number"
											label="Tempo de cache (dias)"
											value={values.cache_ttl_days}
											onChange={handleChange}
											inputProps={{ min: 0 }}
											helperText="Informe um número inteiro em dias (ex.: 30)."
										/>
									</Grid>

									<Grid item md={12} xs={12}>
										<TextField
											fullWidth
											multiline
											minRows={3}
											id="cache_required_response_paths"
											name="cache_required_response_paths"
											label="Campos obrigatórios no retorno (um por linha ou JSON array)"
											value={values.cache_required_response_paths}
											onChange={handleChange}
											placeholder={`Exemplos:

    "RESULTADO.STATUS"
    ou
    ["RESULTADO.STATUS","RESULTADO.CPF"]`}
											helperText={`Formato: ["RESULTADO.CHAVE_VALOR", "RESULTADO.CHAVE_VALOR2", ...]`}
										/>
									</Grid>
								</>
							)}
						</Grid>
					</Grid>
					<Grid item md={12} xs={12} style={{ width: "100%" }}>
						<Grid item md={12} xs={12} style={{ marginTop: 32 }}>
							CONFIGURAÇÕES DE FALHA (FALLBACK E RECONSULTA)
						</Grid>
						<br />
						<Grid container spacing={2}>
							<Grid item md={12} xs={12}>
								<Typography
									variant="body2"
									color="text.secondary"
									style={{ marginBottom: 16 }}
								>
									Defina quando ESTA API conta como "falhou". Por padrão, usada
									pelo wiring de fallback cadastrado na tela do Produto (seção
									"Fallback entre APIs") quando esta API for a raiz de algum
									fallback. Marque "Permitir reconsulta" em uma condição para,
									em vez de disparar fallback para outro fornecedor, liberar o
									botão de reconsulta para o usuário. Deixe sem nenhuma condição
									se esta API nunca é raiz de fallback.
								</Typography>
								<FailureConditionsBuilder
									rules={values.failure_rule}
									onChange={(rules) => setFieldValue("failure_rule", rules)}
								/>
							</Grid>
						</Grid>
					</Grid>
					<Grid
						item
						md={12}
						xs={12}
						alignItems="center"
						alignContent="center"
						marginTop={5}
						marginBottom={5}
					>
						<Button
							variant="contained"
							size="large"
							color="success"
							type="submit"
							disabled={
								values.name.length === 0 ||
								values.url.length === 0 ||
								parameters.length === 0
							}
						>
							{id ? "Atualizar" : "Cadastrar"}
						</Button>
					</Grid>
				</Grid>
			</form>
			{id && isAdmin && <DefaultFailureMessageSection apiId={id} />}
		</>
	);
}
